/**
 * Control-flow signal thrown from the bidder session funnel when an
 * authenticated request resolves to a customer whose account standing is
 * `banned` (BUG-973). Caught by `apiHandler` (`@auctogon/observability`),
 * which converts it into a `{ success: false, error }` envelope at HTTP
 * **418** — a sentinel status the bidder api client traps app-wide to route
 * the user to a dedicated "account unavailable" screen instead of the generic
 * stale-session login bounce (BUG-802's 401 path).
 *
 * Why 418 (and not 401/403): 401 = no/expired session → "log back in"; 403 =
 * authenticated-but-forbidden (a permission denial the app never redirects on).
 * A live-ban denial is neither — the session is valid but the account is
 * unavailable, and offering a plain login is pointless. 418 is unused and
 * unambiguous, so it's safe to trap globally in the fetch executor.
 *
 * Like `ApiValidationError`, this is a routine, user-actionable outcome — NOT
 * an unhandled server error. `apiHandler` maps it BEFORE the generic 500
 * branch and does NOT log it through the logged error path (per ADR 005 /
 * `.claude/rules/structured-logging.md`).
 *
 * SECURITY: the `message` is surfaced verbatim to the client. It MUST stay a
 * static developer-authored string with no internal detail (per AC 1's
 * "generic account unavailable message, no internal detail leaked").
 */
export class AccountBannedError extends Error {
  readonly status = 418 as const;

  /**
   * @param message - User-actionable, developer-authored static string.
   *   Surfaced verbatim in the 418 envelope's `error` field. Must NOT be
   *   derived from a caught error, DB output, or user input.
   */
  constructor(message = 'Account unavailable. Please contact the auction house.') {
    super(message);
    this.name = 'AccountBannedError';
  }
}
